// =============================================================================
// selectors/goals.ts — period targets, for ui/.
// -----------------------------------------------------------------------------
// ⚠ `ui/` may not import `domain/` at all.
//
// ── 🚨 THE POPULATION IS THE WHOLE POINT, AND IT IS NOT THE TAB'S ──
//
// Every other card on the Dashboard measures the ACTIVE RANGE. This one does
// not, and the old client is explicit about why: "a daily $ target needs to
// measure today's P&L even when the user has filtered the journal to last
// month." So the goals card gets its own read, sized to the widest window any
// of the user's goals asks for, and it ignores `bounds` entirely.
//
// ⚠ It does NOT ignore the ACCOUNT filter. Goals intersect the account
// selection (four cases, see `goalAccountScope`) and apply `countsForMetrics`,
// so a SIM account cannot move a target. Ignoring the date filter and ignoring
// the account filter look like the same simplification and are opposite
// decisions — sixth D77 surface, and the first where one axis is dropped and
// the other kept.
// =============================================================================

import { countsForMetrics } from '@domain/trade';
import {
  metricsContextFactory,
  type AccountFilterAccount, type AccountFilterTrade, type AccountSelection,
} from './accounts';

export {
  earliestGoalStart, goalAccountLabel, goalAccountScope, goalProgress,
  goalWindow, visibleGoals,
} from '@domain/goals';

/**
 * The metrics gate `goalProgress` applies per trade.
 *
 * ⚠ NOT `tradesCountingForMetrics`, and the difference matters. That helper
 * applies `tradesForSelection` FIRST — "what is the user looking at" — and a
 * goal does not ask that question. A goal has its OWN account rule, the
 * four-case intersection in `goalAccountScope`, and pre-narrowing by the filter
 * would apply the selection twice: once as beta intends and once as a blanket
 * cut that turns "goal tagged to account X, filter on account Y" into an empty
 * set for the wrong reason.
 *
 * So this exposes only the second half of the pair — the exclude-from-metrics
 * rule that stops a SIM account moving a target — and leaves the narrowing to
 * the goal.
 */
export function goalMetricsGate(
  accounts: readonly AccountFilterAccount[],
  selection: AccountSelection,
): (t: AccountFilterTrade) => boolean {
  const ctxFor = metricsContextFactory(accounts, selection);
  return (t) => countsForMetrics(t, ctxFor(t));
}

export type {
  Goal, GoalJournalDay, GoalPeriod, GoalProgress, GoalTemplate, GoalTrade, GoalWindow,
} from '@domain/goals';
